import { useTranslation } from "react-i18next";
import { useUserSlice } from "@/models/useUserSlice";
import { CloseIcon } from "@/components/Icons";

const ReaderSettingsSheet = ({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const readerSettings = useUserSlice((s) => s.readerSettings);
  const setReaderSettings = useUserSlice
    .getState()
    .preference().setReaderSettings;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[100] bg-[var(--bg-primary)]/50 backdrop-blur-sm transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[101] max-h-[85dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl transition-transform duration-300 ease-out ${
          visible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Header */}
        <div className="pt-6 pb-4 px-6 flex items-center justify-between sticky top-0 bg-[var(--glass-bg)]/90 backdrop-blur-2xl z-10">
          <h3 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
            {t("reader.settings")}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-black/5 dark:bg-white/10 active:scale-90 transition-transform"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="px-6 pt-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] space-y-6">
          {/* Font Size */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text-secondary)]">
                {t("reader.fontSize")}
              </span>
              <span className="text-sm text-[var(--text-muted)]">
                {readerSettings.fontSize}px
              </span>
            </div>
            <input
              type="range"
              min={14}
              max={28}
              step={1}
              value={readerSettings.fontSize}
              onChange={(e) =>
                setReaderSettings({ fontSize: Number(e.target.value) })
              }
              className="w-full accent-[var(--accent)]"
            />
            <div className="flex justify-between text-[10px] text-[var(--text-muted)]">
              <span>A</span>
              <span className="text-lg">A</span>
            </div>
          </div>

          {/* Line Height */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text-secondary)]">
                {t("reader.lineHeight")}
              </span>
              <span className="text-sm text-[var(--text-muted)]">
                {readerSettings.lineHeight.toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min={1.2}
              max={2.4}
              step={0.1}
              value={readerSettings.lineHeight}
              onChange={(e) =>
                setReaderSettings({ lineHeight: Number(e.target.value) })
              }
              className="w-full accent-[var(--accent)]"
            />
          </div>

          {/* Font Family */}
          <div className="space-y-3">
            <span className="text-sm font-medium text-[var(--text-secondary)] mb-2 block">
              {t("reader.fontFamily")}
            </span>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  {
                    value: "serif",
                    label: t("reader.serif"),
                    font: "Georgia, serif",
                  },
                  {
                    value: "sans-serif",
                    label: t("reader.sansSerif"),
                    font: "system-ui, sans-serif",
                  },
                  {
                    value: "monospace",
                    label: t("reader.monospace"),
                    font: "monospace",
                  },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setReaderSettings({ fontFamily: opt.value })}
                  className={`py-3 rounded-xl text-sm font-medium transition-all ${
                    readerSettings.fontFamily === opt.value
                      ? "bg-[var(--accent)] text-[var(--text-inverse)]"
                      : "bg-[var(--glass-bg)] text-[var(--text-secondary)]"
                  }`}
                  style={{ fontFamily: opt.font }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReaderSettingsSheet;
